/**
 * Files Polling Hook
 * Manages file upload, status polling, and document tracking
 *
 * Flow:
 * 1. Upload file (PDF) with images → POST /files → JobResponseDto
 * 2. Poll file status batch every 5s (POST /files/status/batch/get)
 * 3. FileStatusDto includes documents[] — no separate document polling needed
 * 4. Stop polling a file when:
 *    - File has errorCode
 *    - All documents have (isProcessingEndStateReached=true OR errorCode)
 *    - File has no documents and reached final status
 *
 * Progress logic:
 * - 0%: during upload (before API response)
 * - 30%: file record exists (Pending/Uploaded)
 * - 50%: documents exist, until parsed
 * - 50-90%: +15% for each new currentStepCode change (capped at 90%)
 * - 100%: isProcessingEndStateReached=true → success UI
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import type { UploadStatus } from "@/components/application/upload-status/upload-status-card";
import { convertPDFToImages } from "@/lib/pdf-converter";
import { getFileStatusConfig } from "@/lib/status-mapping";
import { ApiException } from "@/services/api";
import { useDocumentsApi } from "@/services/api/documents-api";
import { useFilesApi } from "@/services/api/files-api";
import { DocumentStatusEnum } from "@/types/api/documents";
import { type FileStatusDto, FileStatusEnum, type FileStorageResponseDto, isFileFinalStatus } from "@/types/api/files";
import type { DocumentDisplayData, FileStorageState } from "@/types/upload-file";
import { createSanitizedFile } from "@/utils/file-validation";

// =============================================================================
// CONSTANTS
// =============================================================================

const FILE_POLL_INTERVAL = 5000; // 5 seconds
const MAX_POLL_ERRORS = 5; // Stop polling after consecutive errors

// =============================================================================
// TYPES
// =============================================================================

interface PollState {
    fileIds: Set<number>;
    errorCounts: Map<number, number>;
}

// =============================================================================
// PROGRESS HELPERS
// =============================================================================

/**
 * Calculate document progress based on the new percentage logic.
 * Uses seenStepCodes to track how many unique currentStepCode values we've seen.
 */
function calculateDocumentProgress(doc: DocumentDisplayData, seenStepCodes: Set<string>): number {
    // If there's an error, show 100%
    if (doc.errorCode) return 100;

    // If processing end state reached, show 100%
    if (doc.isProcessingEndStateReached) return 100;

    // Base: documents exist = 50%
    let progress = 50;

    // Add 15% per unique currentStepCode seen (capped at 90%)
    const stepCount = seenStepCodes.size;
    progress = Math.min(50 + stepCount * 15, 90);

    return progress;
}

/**
 * Determine the UI status for a document card
 */
function getDocumentUiStatus(doc: DocumentDisplayData): UploadStatus {
    // Error takes priority
    if (doc.errorCode) return "error";

    // Processing end state reached = success
    if (doc.isProcessingEndStateReached) {
        // Check if the document was exported
        if (doc.status === DocumentStatusEnum.Exported) return "exported";
        if (doc.status === DocumentStatusEnum.Queued) return "success";
        return "success";
    }

    // Still processing
    return "processing";
}

/**
 * Determine the UI status for a file card (no documents yet)
 */
function getFileUiStatus(fileStatus: FileStatusEnum | "uploading", errorCode?: string): UploadStatus {
    if (errorCode) return "error";
    return getFileStatusConfig(fileStatus).uiState;
}

/**
 * Get the progress for a file-level card (no documents yet)
 */
function getFileProgress(fileStatus: FileStatusEnum | "uploading", errorCode?: string): number {
    if (errorCode) return 100;
    // 0% during upload, 30% once file record exists
    if (fileStatus === "uploading") return 0;
    return 30;
}

/**
 * Determine if a file should stop polling
 */
function shouldFileStopPolling(statusDto: FileStatusDto): boolean {
    // File-level error
    if (statusDto.errorCode) return true;

    const hasDocuments = statusDto.documents && statusDto.documents.length > 0;

    if (hasDocuments) {
        // Stop if ALL documents have reached end state or have errors
        const allDocsDone = statusDto.documents.every((doc) => doc.isProcessingEndStateReached || !!doc.errorCode);
        return allDocsDone;
    }

    // No documents — stop if file reached a final status
    return isFileFinalStatus(statusDto.status);
}

/**
 * Determine if a file needs polling (for initial load)
 */
function fileNeedsPolling(file: FileStorageResponseDto): boolean {
    // File-level error — no polling needed
    if (file.errorCode) return false;

    const hasDocuments = file.documents && file.documents.length > 0;

    if (hasDocuments) {
        // Need polling if any document hasn't reached end state and has no error
        return file.documents.some((doc) => doc.isInbound && !doc.isProcessingEndStateReached && !doc.errorCode);
    }

    // No documents — need polling if file isn't in a final status
    return !isFileFinalStatus(file.status);
}

// =============================================================================
// HOOK
// =============================================================================

export function useFilesPolling() {
    const [files, setFiles] = useState<FileStorageState[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const filesApi = useFilesApi();
    const documentsApi = useDocumentsApi();

    // Polling state refs
    const pollStateRef = useRef<PollState>({
        fileIds: new Set(),
        errorCounts: new Map(),
    });

    const filePollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isPollInProgressRef = useRef(false);

    // Track seen step codes per document for progress calculation
    const seenStepCodesRef = useRef<Map<number, Set<string>>>(new Map());

    // Ref to hold polling function — ensures interval always calls latest version
    const pollFilesRef = useRef<(() => Promise<void>) | null>(null);

    // =============================================================================
    // BEFOREUNLOAD WARNING
    // =============================================================================

    useEffect(() => {
        if (!isUploading) return;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            return "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isUploading]);

    // =============================================================================
    // BLOCK SPA NAVIGATION
    // =============================================================================

    const blocker = useBlocker(({ currentLocation, nextLocation }) => isUploading && currentLocation.pathname !== nextLocation.pathname);

    // =============================================================================
    // HELPERS
    // =============================================================================

    /**
     * Track a document's currentStepCode and return the updated set
     */
    const trackStepCode = useCallback((docId: number, stepCode?: string | null): Set<string> => {
        if (!seenStepCodesRef.current.has(docId)) {
            seenStepCodesRef.current.set(docId, new Set());
        }
        const codes = seenStepCodesRef.current.get(docId)!;
        if (stepCode) {
            codes.add(stepCode);
        }
        return codes;
    }, []);

    /**
     * Convert a DocumentListItemDto-like object to DocumentDisplayData with progress
     */
    const toDocumentDisplayData = useCallback(
        (
            doc: {
                id: number;
                status: string;
                documentType?: string;
                fileName?: string;
                generatedName: string;
                createdAt: string;
                isInbound?: boolean | null;
                isProcessingEndStateReached: boolean;
                currentStepCode?: string | null;
                errorCode?: string;
                errorMessage?: string;
                hasRetryWarning?: boolean;
            },
            existingDoc?: DocumentDisplayData,
        ): DocumentDisplayData => {
            // Track step code for progress calculation
            trackStepCode(doc.id, doc.currentStepCode);

            const displayData: DocumentDisplayData = {
                id: doc.id,
                status: doc.status as DocumentStatusEnum,
                documentType: doc.documentType,
                fileName: doc.fileName,
                generatedName: doc.generatedName,
                createdAt: doc.createdAt,
                isInbound: doc.isInbound,
                errorCode: doc.errorCode,
                errorMessage: doc.errorMessage,
                hasRetryWarning: doc.hasRetryWarning,
                isProcessingEndStateReached: doc.isProcessingEndStateReached,
                currentStepCode: doc.currentStepCode,
                // Preserve existing display fields (vendor, amount, etc.)
                vendor: existingDoc?.vendor,
                documentNumber: existingDoc?.documentNumber,
                date: existingDoc?.date,
                amount: existingDoc?.amount,
                description: existingDoc?.description,
            };

            return displayData;
        },
        [trackStepCode],
    );

    /**
     * Convert API FileStorageResponseDto to UI FileStorageState
     */
    const toFileStorageState = useCallback(
        (apiFile: FileStorageResponseDto, existingState?: Partial<FileStorageState>): FileStorageState => {
            const hasDocuments = apiFile.documents && apiFile.documents.length > 0;

            // Convert documents
            const documents: DocumentDisplayData[] = apiFile.documents.map((doc) =>
                toDocumentDisplayData(
                    doc,
                    existingState?.documents?.find((d) => d.id === doc.id),
                ),
            );

            // Determine if we should show document cards
            const showDocumentCards = hasDocuments && documents.some((d) => d.isInbound === true);

            // Determine file-level UI status and progress
            let uiStatus: UploadStatus;
            let progress: number;
            let statusLabel: string;

            if (apiFile.hasRetryWarning) {
                uiStatus = "processing";
                progress = 50;
                statusLabel = "Retrying...";
            } else if (apiFile.errorCode) {
                uiStatus = "error";
                progress = 100;
                statusLabel = "Processing failed";
            } else if (hasDocuments) {
                // File has documents — file card UI is based on aggregate document state
                const allDocsDone = documents.every((d) => d.isProcessingEndStateReached || !!d.errorCode);
                const anyDocError = documents.some((d) => !!d.errorCode);
                const allDocsExported = documents.every((d) => d.status === DocumentStatusEnum.Exported);

                if (allDocsDone) {
                    if (allDocsExported) {
                        uiStatus = "exported";
                        progress = 100;
                        statusLabel = "Exported";
                    } else if (anyDocError) {
                        uiStatus = "error";
                        progress = 100;
                        statusLabel = "Processing failed";
                    } else {
                        uiStatus = "success";
                        progress = 100;
                        statusLabel = "Completed";
                    }
                } else {
                    uiStatus = "processing";
                    progress = 50;
                    statusLabel = "Processing...";
                }
            } else {
                // No documents yet — use file status
                uiStatus = getFileUiStatus(apiFile.status, apiFile.errorCode);
                progress = getFileProgress(apiFile.status, apiFile.errorCode);
                statusLabel = getFileStatusConfig(apiFile.status).label;
            }

            return {
                id: existingState?.id || apiFile.id.toString(),
                fileStorageId: apiFile.id,
                fileName: apiFile.fileName,
                fileSizeBytes: apiFile.fileSizeBytes,
                contentType: apiFile.contentType,
                pageCount: apiFile.pageCount,
                fileStatus: apiFile.status,
                uiStatus,
                progress,
                statusLabel,
                uploadedAt: apiFile.uploadedAt,
                documents,
                showDocumentCards,
                createdAt: existingState?.createdAt || new Date(apiFile.uploadedAt),
                errorCode: apiFile.errorCode,
                errorMessage: apiFile.errorMessage,
                hasRetryWarning: apiFile.hasRetryWarning,
            };
        },
        [toDocumentDisplayData],
    );

    // =============================================================================
    // FILE POLLING
    // =============================================================================

    /**
     * Poll all active files using batch endpoint
     * Uses POST /files/status/batch/get to fetch all statuses in a single request
     */
    const pollFiles = useCallback(async () => {
        const { fileIds, errorCounts } = pollStateRef.current;
        if (fileIds.size === 0 || isPollInProgressRef.current) return;

        const fileIdsArray = Array.from(fileIds);
        isPollInProgressRef.current = true;

        try {
            const batchResponse = await filesApi.getFileStatusBatch(fileIdsArray);

            const filesToStopPolling: number[] = [];

            // Process successful statuses
            batchResponse.statuses.forEach((statusDto) => {
                const fileId = statusDto.id;

                // Reset error count for successfully fetched files
                errorCounts.delete(fileId);

                // Check if file should stop polling
                if (shouldFileStopPolling(statusDto)) {
                    filesToStopPolling.push(fileId);
                }
            });

            // Handle not found files
            batchResponse.notFoundIds.forEach((fileId) => {
                const currentCount = errorCounts.get(fileId) || 0;
                errorCounts.set(fileId, currentCount + 1);

                if (currentCount + 1 >= MAX_POLL_ERRORS) {
                    console.error(`File ${fileId} polling stopped after ${MAX_POLL_ERRORS} not found errors`);
                    filesToStopPolling.push(fileId);
                }
            });

            // Remove files from polling
            filesToStopPolling.forEach((fileId) => {
                fileIds.delete(fileId);
            });

            // Update React state
            setFiles((prevFiles) => {
                const updatedFiles = [...prevFiles];

                batchResponse.statuses.forEach((statusDto) => {
                    const fileId = statusDto.id;
                    const fileIndex = updatedFiles.findIndex((f) => f.fileStorageId === fileId);
                    if (fileIndex === -1) return;

                    const existingFile = updatedFiles[fileIndex];
                    const hasDocuments = statusDto.documents && statusDto.documents.length > 0;

                    // Convert documents
                    const documents: DocumentDisplayData[] =
                        statusDto.documents?.map((doc) =>
                            toDocumentDisplayData(
                                doc,
                                existingFile.documents.find((d) => d.id === doc.id),
                            ),
                        ) || existingFile.documents;

                    // Determine showDocumentCards
                    const showDocumentCards = hasDocuments && documents.some((d) => d.isInbound === true);

                    // Determine file-level UI state
                    let uiStatus: UploadStatus;
                    let progress: number;
                    let statusLabel: string;

                    if (statusDto.hasRetryWarning) {
                        uiStatus = "processing";
                        progress = 50;
                        statusLabel = "Retrying...";
                    } else if (statusDto.errorCode) {
                        uiStatus = "error";
                        progress = 100;
                        statusLabel = "Processing failed";
                    } else if (hasDocuments) {
                        const allDocsDone = documents.every((d) => d.isProcessingEndStateReached || !!d.errorCode);
                        const anyDocError = documents.some((d) => !!d.errorCode);
                        const allDocsExported = documents.every((d) => d.status === DocumentStatusEnum.Exported);

                        if (allDocsDone) {
                            if (allDocsExported) {
                                uiStatus = "exported";
                                progress = 100;
                                statusLabel = "Exported";
                            } else if (anyDocError) {
                                uiStatus = "error";
                                progress = 100;
                                statusLabel = "Processing failed";
                            } else {
                                uiStatus = "success";
                                progress = 100;
                                statusLabel = "Completed";
                            }
                        } else {
                            uiStatus = "processing";
                            progress = 50;
                            statusLabel = "Processing...";
                        }
                    } else {
                        uiStatus = getFileUiStatus(statusDto.status, statusDto.errorCode);
                        progress = getFileProgress(statusDto.status, statusDto.errorCode);
                        statusLabel = getFileStatusConfig(statusDto.status).label;
                    }

                    updatedFiles[fileIndex] = {
                        ...existingFile,
                        fileStatus: statusDto.status,
                        uiStatus,
                        progress,
                        statusLabel,
                        documents,
                        showDocumentCards,
                        errorCode: statusDto.errorCode || existingFile.errorCode,
                        errorMessage: statusDto.errorMessage || existingFile.errorMessage,
                        hasRetryWarning: statusDto.hasRetryWarning,
                    };
                });

                // Handle not found files that exceeded error count
                batchResponse.notFoundIds.forEach((fileId) => {
                    const count = errorCounts.get(fileId) || 0;
                    if (count >= MAX_POLL_ERRORS) {
                        const idx = updatedFiles.findIndex((f) => f.fileStorageId === fileId);
                        if (idx !== -1) {
                            updatedFiles[idx] = {
                                ...updatedFiles[idx],
                                uiStatus: "error" as UploadStatus,
                                progress: 100,
                                errorCode: "NotFoundFile",
                            };
                        }
                    }
                });

                return updatedFiles;
            });

            // Stop polling if no more files
            if (fileIds.size === 0 && filePollingIntervalRef.current) {
                clearInterval(filePollingIntervalRef.current);
                filePollingIntervalRef.current = null;
            }
        } catch (error) {
            console.error("Batch file polling error:", error);
            // On batch API error, increment error counts for all files
            fileIdsArray.forEach((fileId) => {
                const currentCount = errorCounts.get(fileId) || 0;
                errorCounts.set(fileId, currentCount + 1);

                if (currentCount + 1 >= MAX_POLL_ERRORS) {
                    console.error(`File ${fileId} polling stopped after ${MAX_POLL_ERRORS} errors`);
                    fileIds.delete(fileId);
                }
            });
        } finally {
            isPollInProgressRef.current = false;
        }
    }, [filesApi, toDocumentDisplayData]);

    // Keep ref updated so intervals always call latest version
    pollFilesRef.current = pollFiles;

    /**
     * Start file polling interval
     */
    const startFilePolling = useCallback(() => {
        if (filePollingIntervalRef.current) return;

        // Poll immediately
        pollFilesRef.current?.();

        // Then set interval
        filePollingIntervalRef.current = setInterval(() => {
            pollFilesRef.current?.();
        }, FILE_POLL_INTERVAL);
    }, []);

    // =============================================================================
    // UPLOAD
    // =============================================================================

    /**
     * Upload files
     */
    const uploadFiles = useCallback(
        async (fileList: FileList) => {
            const originalFiles = Array.from(fileList);

            // Sanitize all file names upfront before any processing
            const filesToUpload = originalFiles.map((file) => createSanitizedFile(file));

            // Track uploading state for beforeunload warning
            setIsUploading(true);

            // Create initial UI state for each file
            const newFiles: FileStorageState[] = filesToUpload.map((file) => ({
                id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                fileName: file.name,
                fileSizeBytes: file.size,
                contentType: file.type,
                fileStatus: "uploading" as const,
                uiStatus: "uploading" as UploadStatus,
                progress: 0,
                statusLabel: "Uploading...",
                documents: [],
                showDocumentCards: false,
                createdAt: new Date(),
            }));

            setFiles((prev) => [...newFiles, ...prev]);

            // Generate a single batch id for this upload click so backend logs can
            // group all files together and emit a per-batch summary. crypto.randomUUID
            // is available in all modern browsers and Node test envs.
            const batchId =
                typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            const totalFiles = filesToUpload.length;

            // Upload each file
            for (let i = 0; i < filesToUpload.length; i++) {
                const file = filesToUpload[i];
                const fileState = newFiles[i];

                try {
                    // Convert PDF to images
                    let images: File[] = [];
                    if (file.type === "application/pdf") {
                        try {
                            const pdfUrl = URL.createObjectURL(file);
                            try {
                                images = await convertPDFToImages(pdfUrl, file);
                            } finally {
                                URL.revokeObjectURL(pdfUrl);
                            }
                        } catch (err) {
                            console.warn("Failed to convert PDF to images, uploading without images:", err);
                        }
                    }

                    // Upload file (legacy upload flow — no org selection here)
                    const jobResponse = await filesApi.uploadFile(file, images, images.length || undefined, undefined, {
                        batchId,
                        fileIndex: i + 1,
                        totalFiles,
                    });

                    // Update state with API response — file record exists, progress = 30%
                    setFiles((prev) =>
                        prev.map((f) => {
                            if (f.id === fileState.id) {
                                return {
                                    ...f,
                                    fileStorageId: jobResponse.fileStorageId,
                                    fileStatus: FileStatusEnum.Pending,
                                    uiStatus: "queued" as UploadStatus,
                                    progress: 30,
                                    statusLabel: "Queued...",
                                };
                            }
                            return f;
                        }),
                    );

                    // Add to polling
                    pollStateRef.current.fileIds.add(jobResponse.fileStorageId);

                    // Start polling if not already running
                    if (!filePollingIntervalRef.current) {
                        startFilePolling();
                    }
                } catch (error) {
                    console.error(`Failed to upload ${file.name}:`, error);

                    // Capture error details from ApiException if available
                    const errorResponse = error instanceof ApiException ? error.errorResponse : undefined;
                    const errorCode = errorResponse?.code || "Unknown";

                    // Update state with error
                    setFiles((prev) =>
                        prev.map((f) => {
                            if (f.id === fileState.id) {
                                return {
                                    ...f,
                                    fileStatus: FileStatusEnum.Failed,
                                    uiStatus: "error" as UploadStatus,
                                    progress: 100,
                                    statusLabel: "Upload failed",
                                    errorCode,
                                };
                            }
                            return f;
                        }),
                    );
                }
            }

            // All uploads complete (success or failure) — allow page navigation
            setIsUploading(false);
        },
        [filesApi, startFilePolling],
    );

    // =============================================================================
    // INITIAL LOAD
    // =============================================================================

    /**
     * Fetch existing files on load
     */
    const fetchExistingFiles = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await filesApi.getFiles({ pageSize: 100 });

            // Filter out deleted files
            const activeFiles = response.data.filter((f) => f.status !== FileStatusEnum.Deleted);

            // Convert to UI state
            const fileStates = activeFiles.map((apiFile) => toFileStorageState(apiFile));
            setFiles(fileStates);

            // Start polling for files that need it
            activeFiles.forEach((apiFile) => {
                if (fileNeedsPolling(apiFile)) {
                    pollStateRef.current.fileIds.add(apiFile.id);
                }
            });

            // Start polling interval if needed
            if (pollStateRef.current.fileIds.size > 0) {
                startFilePolling();
            }
        } catch (error) {
            console.error("Failed to fetch existing files:", error);
        } finally {
            setIsLoading(false);
        }
    }, [filesApi, toFileStorageState, startFilePolling]);

    // =============================================================================
    // DELETE
    // =============================================================================

    /**
     * Delete a file
     */
    const deleteFile = useCallback(
        async (fileId: string) => {
            const file = files.find((f) => f.id === fileId);
            if (!file) return;

            // Remove from UI immediately
            setFiles((prev) => prev.filter((f) => f.id !== fileId));

            // Stop polling
            if (file.fileStorageId) {
                pollStateRef.current.fileIds.delete(file.fileStorageId);
            }

            // Call API to delete
            if (file.fileStorageId) {
                try {
                    await filesApi.deleteFile(file.fileStorageId);
                    await fetchExistingFiles();
                } catch (error) {
                    console.error("Failed to delete file:", error);
                    await fetchExistingFiles();
                }
            }
        },
        [files, filesApi, fetchExistingFiles],
    );

    /**
     * Delete a document
     */
    const deleteDocument = useCallback(
        async (documentId: number) => {
            // Clean up step code tracking
            seenStepCodesRef.current.delete(documentId);

            // Remove from UI
            setFiles((prev) =>
                prev.map((file) => ({
                    ...file,
                    documents: file.documents.filter((d) => d.id !== documentId),
                })),
            );

            // Call API
            try {
                await documentsApi.deleteDocument(documentId);
                await fetchExistingFiles();
            } catch (error) {
                console.error("Failed to delete document:", error);
                await fetchExistingFiles();
            }
        },
        [documentsApi, fetchExistingFiles],
    );

    // =============================================================================
    // CLEANUP
    // =============================================================================

    useEffect(() => {
        const pollState = pollStateRef.current;
        const seenStepCodes = seenStepCodesRef.current;
        return () => {
            if (filePollingIntervalRef.current) {
                clearInterval(filePollingIntervalRef.current);
                filePollingIntervalRef.current = null;
            }
            isPollInProgressRef.current = false;
            pollState.fileIds.clear();
            pollState.errorCounts.clear();
            seenStepCodes.clear();
        };
    }, []);

    // =============================================================================
    // RETURN
    // =============================================================================

    return useMemo(
        () => ({
            files,
            isLoading,
            isUploading,
            blocker,
            uploadFiles,
            deleteFile,
            deleteDocument,
            fetchExistingFiles,
            // Expose progress calculation for use in LegacyHomePage
            calculateDocumentProgress,
            getDocumentUiStatus,
            trackStepCode,
            seenStepCodesRef,
        }),
        [files, isLoading, isUploading, blocker, uploadFiles, deleteFile, deleteDocument, fetchExistingFiles, trackStepCode],
    );
}
